import React from 'react';
import LegalDoc from '../../components/LegalDoc';

export default function IncidentResponse() {
  return (
    <LegalDoc
      slug="incident-response"
      title="Incident Response Playbook"
      subtitle="Severity classification, on-call rotation, customer-comms templates, post-mortem requirements."
      version="1.0"
      lastReviewed="2026-02-18"
      controls={["CC7.2"]}
    >
      <h2>Severity classification</h2>
      <table>
        <thead><tr><th>Sev</th><th>Definition</th><th>MTTA target</th><th>MTTR target</th><th>Customer comms</th></tr></thead>
        <tbody>
          <tr><td><strong>SEV-1</strong></td><td>Production outage; all customers affected; or any confirmed data loss/leak</td><td>15 min</td><td>4 hr</td><td>Status page + email within 60 min</td></tr>
          <tr><td><strong>SEV-2</strong></td><td>Single critical flow broken (auth, billing, savings calculation); &gt;25% of customers affected</td><td>1 hr</td><td>1 business day</td><td>Status page within 2 hr; email if &gt; 4hr</td></tr>
          <tr><td><strong>SEV-3</strong></td><td>Non-critical feature degraded; ≤ 25% of customers affected</td><td>4 hr</td><td>3 business days</td><td>In-product banner; no email</td></tr>
          <tr><td><strong>SEV-4</strong></td><td>Cosmetic; no customer-facing impact</td><td>1 business day</td><td>2 weeks</td><td>None</td></tr>
        </tbody>
      </table>

      <h2>On-call</h2>
      <p>Single-founder stage (Feb 2026): Founder is on-call 24/7. Slack-paged via PagerDuty-equivalent webhook (Sentry alerts → Slack #alerts channel with @here mention for SEV-1/2). When team grows beyond 3 engineers, we'll adopt a weekly rotation with PagerDuty primary/secondary.</p>

      <h2>Detection sources</h2>
      <ul>
        <li><strong>Sentry</strong> — backend exceptions; alert if &gt; 10 errors/min for 5 min</li>
        <li><strong>Uptime monitor</strong> — pings <code>/api/health</code> every 30s; alert if 3 consecutive failures</li>
        <li><strong>Customer report</strong> — any email to lakshmi@cartie.ai or in-app support ticket → triaged within MTTA target</li>
        <li><strong>Security audit cron</strong> — weekly run flags anomalies in admin audit log</li>
      </ul>

      <h2>Triage flow</h2>
      <ol>
        <li><strong>Acknowledge</strong> in Slack #alerts within MTTA target. State "I am on it"</li>
        <li><strong>Classify</strong> severity using the table above</li>
        <li><strong>SEV-1 only</strong>: open a war-room thread in #alerts. Tag the incident with a sequential ID (INC-YYYYMMDD-NN)</li>
        <li><strong>Mitigate</strong> first, root-cause second. If the fix is &gt; 30 min, consider feature-flag-off or rollback to last green deploy</li>
        <li><strong>Verify</strong> with the same detection source that originally fired</li>
        <li><strong>Communicate</strong> per the severity-comms table</li>
      </ol>

      <h2>Customer communication templates</h2>

      <h3>SEV-1 status page (within 60 min)</h3>
      <blockquote>
        <strong>[INVESTIGATING]</strong> {'{start_time}'} — We're investigating reports of {'{what_is_broken}'}. Customers may experience {'{impact}'}. We'll post the next update at {'{now + 30min}'}.
      </blockquote>

      <h3>SEV-1 customer email (post-resolution)</h3>
      <blockquote>
        Subject: Service incident on {'{date}'} — your data is safe<br /><br />
        On {'{date}'} between {'{start}'} and {'{end}'} UTC, CARTIE experienced {'{1-sentence what}'}. {'{Affected feature}'} was unavailable. No customer data was lost or exposed.<br /><br />
        Root cause: {'{1 sentence}'}.<br />
        Fix: {'{1 sentence}'}.<br />
        Prevention: {'{1 sentence describing the controls we are adding}'}.<br /><br />
        We don't think this kind of failure should happen, and we're committed to making it not happen again. Reply to this email if you have any questions; I read every one.<br /><br />
        — Lakshmi
      </blockquote>

      <h2>Post-mortem requirements (mandatory for SEV-1 &amp; SEV-2)</h2>
      <p>Within 5 business days of resolution, the on-call writes a blameless post-mortem with:</p>
      <ul>
        <li>Incident ID + severity + duration</li>
        <li>Timeline (UTC) — every action, every alert</li>
        <li>Root cause (technical) + contributing causes (process, organizational)</li>
        <li>What worked / what didn't</li>
        <li>Action items: each with an owner and a due date (no "TBD"s)</li>
        <li>Customer impact estimate (# customers, $ revenue impact if any)</li>
      </ul>
      <p>Post-mortems are filed in <code>incident_post_mortems</code> and reviewed in the next quarterly Risk Assessment.</p>

      <h2>Practice drills</h2>
      <p>We run a <strong>quarterly</strong> chaos drill (next: 2026-05-18):</p>
      <ol>
        <li>Founder picks an unannounced failure scenario from a hat (e.g., "MongoDB primary down", "Stripe webhook receiver returns 500", "LLM provider rate-limits us")</li>
        <li>Inject the failure in staging (NEVER prod)</li>
        <li>Time how long it takes to: detect → triage → mitigate → notify customers (using the templates above)</li>
        <li>File results in <code>incident_drill_results</code>; update playbook if any gap found</li>
      </ol>

      <h2>Sign-off</h2>
      <p>Playbook approved Feb 18, 2026. Next review: May 18, 2026 (post Q1 chaos drill).</p>
    </LegalDoc>
  );
}
